import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router";
import Seo from "../components/Seo.tsx";
import Divider from "../components/ornaments/Divider.tsx";
import Feather from "../components/ornaments/Feather.tsx";
import { Button, ButtonLink } from "../components/ui/Button.tsx";
import {
  getCreation,
  requestCreationAction,
  ApiError,
  type Creation,
} from "../lib/api.ts";
import { useCart } from "../lib/cart.tsx";

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string; notFound: boolean }
  | { status: "ready"; creation: Creation };

type RequestState =
  | { status: "idle" }
  | { status: "pending"; kind: "order" | "gift" }
  | { status: "error"; message: string }
  | { status: "ordered" };

export default function CreationPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { add } = useCart();
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [request, setRequest] = useState<RequestState>(
    searchParams.get("paid") === "1" ? { status: "ordered" } : { status: "idle" },
  );

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setState({ status: "loading" });
    getCreation(slug)
      .then((creation) => {
        if (!cancelled) setState({ status: "ready", creation });
      })
      .catch((err) => {
        if (cancelled) return;
        const isApi = err instanceof ApiError;
        setState({
          status: "error",
          message: isApi ? err.message : "Could not fetch creation",
          notFound: isApi && err.status === 404,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  function handleOrder() {
    if (!slug || state.status !== "ready") return;
    const c = state.creation;
    add("custom-horoscope-candle", 1, {
      creationSlug: slug,
      customName: c.candleName,
    });
  }

  async function handleGift() {
    if (!slug || request.status === "pending") return;
    setRequest({ status: "pending", kind: "gift" });
    try {
      await requestCreationAction(slug, "gift");
      navigate(`/creation/${slug}/scroll`);
    } catch (err) {
      setRequest({
        status: "error",
        message:
          err instanceof ApiError
            ? err.message
            : "Could not prepare the scroll — please try again.",
      });
    }
  }

  if (state.status === "loading") return <LoadingSkeleton />;
  if (state.status === "error") {
    return (
      <div className="mx-auto max-w-2xl px-6 py-32 text-center md:px-10">
        <span className="eyebrow">Not Found</span>
        <h1 className="mt-6 font-serif text-4xl text-[var(--color-emerald-deep)] md:text-5xl">
          {state.notFound
            ? "This candle has not been composed."
            : "Something went quiet."}
        </h1>
        <p className="mt-6 text-sm text-[var(--color-stone)]">
          {state.message}
        </p>
        <div className="mt-10">
          <ButtonLink to="/create">Compose One of Your Own</ButtonLink>
        </div>
      </div>
    );
  }

  const c = state.creation;
  const created = new Date(c.createdAt * 1000).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="mx-auto max-w-3xl px-6 py-20 md:px-10 md:py-28">
      <Seo
        title={`${c.candleName} — A Candle for ${c.userName}`}
        description={`${c.reading.slice(0, 140)}…`}
        path={`/creation/${slug}`}
      />
      <section className="marble-panel relative overflow-hidden px-8 py-20 md:px-16 md:py-28">
        <Feather
          className="pointer-events-none absolute top-1/2 left-1/2 h-[640px] w-auto -translate-x-1/2 -translate-y-1/2 opacity-[0.08]"
          style={{ animation: "var(--animate-fade-in)" }}
        />
        <Feather
          className="pointer-events-none absolute top-10 right-10 hidden h-36 w-auto rotate-[18deg] opacity-65 md:block"
          style={{
            animation: "var(--animate-fade-up)",
            animationDelay: "400ms",
          }}
        />
        <Feather
          className="pointer-events-none absolute bottom-10 left-10 hidden h-28 w-auto -rotate-[24deg] opacity-55 md:block"
          style={{
            animation: "var(--animate-fade-up)",
            animationDelay: "560ms",
          }}
        />

        <div className="relative flex flex-col items-center text-center">
          <span
            className="eyebrow"
            style={{ animation: "var(--animate-fade-up)" }}
          >
            A Candle for {c.userName} · {c.zodiacSign}
          </span>

          <h1
            className="mt-6 max-w-2xl font-serif text-5xl leading-[1.02] text-[var(--color-emerald-deep)] md:text-7xl"
            style={{
              animation: "var(--animate-fade-up)",
              animationDelay: "140ms",
            }}
          >
            <span className="text-gold-leaf font-serif">{c.candleName}</span>
          </h1>

          <p
            className="mt-5 max-w-md font-serif text-xl italic text-[var(--color-emerald-deep)]/80"
            style={{
              animation: "var(--animate-fade-up)",
              animationDelay: "260ms",
            }}
          >
            {c.theme}
          </p>

          <Divider className="my-12" />

          {/* The reading — prominent parchment card with strong contrast */}
          <article
            className="result-card result-card--parchment w-full max-w-2xl"
            style={{
              animation: "var(--animate-fade-up)",
              animationDelay: "380ms",
            }}
          >
            <span className="result-eyebrow">Your Reading</span>
            <p className="mt-5 font-serif text-xl leading-[1.7] text-[var(--color-charcoal-deep)] md:text-[1.45rem] md:leading-[1.65]">
              {c.reading}
            </p>
          </article>

          {/* The scent story — emerald card with gold rules, highly legible */}
          <article
            className="result-card result-card--emerald mt-10 w-full max-w-2xl"
            style={{
              animation: "var(--animate-fade-up)",
              animationDelay: "500ms",
            }}
          >
            <span className="result-eyebrow result-eyebrow--on-dark">
              The Scent Story
            </span>
            <ul className="mt-7 flex flex-col gap-6 text-left">
              <ScentRow label="Top" value={c.scent.top} />
              <ScentRow label="Heart" value={c.scent.heart} />
              <ScentRow label="Base" value={c.scent.base} />
            </ul>
          </article>

          {/* Dedication — cream card with gold accent for the engraved line */}
          <article
            className="result-card result-card--dedication mt-10 w-full max-w-xl"
            style={{
              animation: "var(--animate-fade-up)",
              animationDelay: "620ms",
            }}
          >
            <span className="result-eyebrow">For the Jar</span>
            <p className="mt-6 font-serif text-2xl italic leading-snug text-[var(--color-emerald-deep)] md:text-3xl">
              “{c.dedication}”
            </p>
          </article>

          <p
            className="mt-12 text-[0.65rem] uppercase tracking-[0.18em] text-[var(--color-stone)]"
            style={{
              animation: "var(--animate-fade-up)",
              animationDelay: "760ms",
            }}
          >
            Composed {created}
          </p>
        </div>
      </section>

      {/* Two CTAs + a link to compose another */}
      <div
        className="mt-14 flex flex-col items-center gap-6 text-center"
        style={{
          animation: "var(--animate-fade-up)",
          animationDelay: "180ms",
        }}
      >
        {request.status === "ordered" ? (
          <div className="result-card result-card--parchment w-full max-w-xl">
            <span className="result-eyebrow">Request Received</span>
            <p className="mt-5 font-serif text-xl italic leading-relaxed text-[var(--color-emerald-deep)]">
              Your request for <em>{c.candleName}</em> has been sent to the
              atelier. We will be in touch at the address you provided — expect
              a letter within a day or two.
            </p>
          </div>
        ) : (
          <>
            <p className="font-serif text-xl italic text-[var(--color-stone)]">
              What shall we do with your candle?
            </p>
            <div className="flex flex-col gap-4 sm:flex-row sm:gap-6">
              <Button
                type="button"
                onClick={handleOrder}
                disabled={request.status === "pending"}
              >
                {request.status === "pending" && request.kind === "order"
                  ? "Preparing Checkout…"
                  : "Order This Candle · $45"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={handleGift}
                disabled={request.status === "pending"}
              >
                {request.status === "pending" && request.kind === "gift"
                  ? "Preparing…"
                  : "Send as a Gift"}
              </Button>
            </div>
            {request.status === "error" ? (
              <p className="text-sm italic text-[var(--color-oxblood)]">
                {request.message}
              </p>
            ) : null}
          </>
        )}

        <Link
          to="/create"
          className="mt-8 text-[0.7rem] uppercase tracking-[0.16em] text-[var(--color-stone)] hover:text-[var(--color-emerald)]"
        >
          or compose another candle →
        </Link>
      </div>
    </div>
  );
}

function ScentRow({ label, value }: { label: string; value: string }) {
  return (
    <li className="flex items-baseline gap-6 border-b border-[var(--color-gold)]/25 pb-4 last:border-b-0">
      <span className="w-14 shrink-0 text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-[var(--color-gold-soft)]">
        {label}
      </span>
      <span className="font-serif text-lg italic leading-snug text-[var(--color-cream)] md:text-xl">
        {value}
      </span>
    </li>
  );
}

function LoadingSkeleton() {
  return (
    <div className="mx-auto max-w-3xl px-6 py-32 text-center md:px-10">
      <span className="eyebrow">Composing</span>
      <p
        className="mt-6 font-serif text-3xl italic text-[var(--color-stone)]"
        style={{ animation: "var(--animate-fade-in)" }}
      >
        unwrapping your candle…
      </p>
    </div>
  );
}
